import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  LogOut,
  Users,
  Wallet,
  ClipboardList,
  TrendingUp,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-[#64748B] font-bold">{label}</span>
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={18} />
        </span>
      </div>
      <div className="font-display text-3xl font-bold text-[#022C22] mt-3">{value}</div>
    </div>
  );
}

function fmtINR(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [overview, setOverview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [txns, setTxns] = useState([]);

  const loadAll = async () => {
    try {
      const [meRes, ov, ls, tx] = await Promise.all([
        api.get("/auth/me"),
        api.get("/admin/overview"),
        api.get("/admin/leads"),
        api.get("/admin/transactions"),
      ]);
      setMe(meRes.data);
      setOverview(ov.data);
      setLeads(ls.data);
      setTxns(tx.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    navigate("/login", { replace: true });
  };

  const updateLeadStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Lead updated");
    } catch (e) {
      toast.error("Failed to update lead");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] bg-grain">
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#064E3B] text-white flex items-center justify-center">
              <Sparkles size={18} />
            </div>
            <div>
              <div className="font-display text-lg font-bold text-[#022C22]">GST24 Admin</div>
              <div className="text-xs text-[#64748B]">{me?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {me?.picture && (
              <img src={me.picture} alt="" className="w-8 h-8 rounded-full border border-[#E5E7EB]" />
            )}
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="btn-secondary text-sm"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-bold text-[#022C22] tracking-tight">
          Welcome back, {me?.name?.split(" ")[0] || "Admin"}.
        </h1>
        <p className="text-[#3F6255] mt-2">Here&apos;s what&apos;s happening across leads and orders.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8" data-testid="admin-stats">
          <StatCard icon={Users} label="Total leads" value={overview?.total_leads ?? "—"} color="bg-[#F4F1EA] text-[#064E3B]" />
          <StatCard icon={ClipboardList} label="New leads" value={overview?.new_leads ?? "—"} color="bg-[#E0F2FE] text-[#0EA5E9]" />
          <StatCard icon={Wallet} label="Paid orders" value={overview?.paid_transactions ?? "—"} color="bg-[#D1FAE5] text-[#10B981]" />
          <StatCard icon={TrendingUp} label="Revenue" value={fmtINR(overview?.revenue_inr ?? 0)} color="bg-[#FEF3C7] text-[#B45309]" />
        </div>

        <div className="mt-10">
          <Tabs defaultValue="leads" className="w-full">
            <TabsList className="bg-[#F4F1EA] rounded-full p-1">
              <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-full px-6">Leads</TabsTrigger>
              <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full px-6">Orders</TabsTrigger>
            </TabsList>

            <TabsContent value="leads" className="mt-6">
              <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
                <div className="grid grid-cols-12 text-xs uppercase tracking-widest text-[#64748B] font-bold px-4 py-3 border-b border-[#E5E7EB] bg-[#FDFBF7]">
                  <div className="col-span-3">Name</div>
                  <div className="col-span-3">Contact</div>
                  <div className="col-span-3">Service</div>
                  <div className="col-span-3">Status</div>
                </div>
                {leads.length === 0 && (
                  <div className="p-8 text-center text-[#64748B]">No leads yet.</div>
                )}
                {leads.map((l) => (
                  <div key={l.id} className="grid grid-cols-12 items-center px-4 py-4 border-b border-[#F4F1EA] text-sm" data-testid={`lead-row-${l.id}`}>
                    <div className="col-span-3">
                      <div className="font-medium text-[#022C22]">{l.name}</div>
                      <div className="text-xs text-[#64748B]">{new Date(l.created_at).toLocaleString()}</div>
                    </div>
                    <div className="col-span-3 text-[#3F6255]">
                      <div>{l.email}</div>
                      <div className="text-xs">{l.phone}</div>
                    </div>
                    <div className="col-span-3 text-[#022C22]">{l.service_interested}</div>
                    <div className="col-span-3">
                      <Select value={l.status} onValueChange={(v) => updateLeadStatus(l.id, v)}>
                        <SelectTrigger className="w-44" data-testid={`lead-status-${l.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="orders" className="mt-6">
              <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
                <div className="grid grid-cols-12 text-xs uppercase tracking-widest text-[#64748B] font-bold px-4 py-3 border-b border-[#E5E7EB] bg-[#FDFBF7]">
                  <div className="col-span-4">Plan</div>
                  <div className="col-span-3">Customer</div>
                  <div className="col-span-2">Amount</div>
                  <div className="col-span-3">Status</div>
                </div>
                {txns.length === 0 && (
                  <div className="p-8 text-center text-[#64748B]">No orders yet.</div>
                )}
                {txns.map((t) => (
                  <div key={t.session_id} className="grid grid-cols-12 items-center px-4 py-4 border-b border-[#F4F1EA] text-sm">
                    <div className="col-span-4">
                      <div className="font-medium text-[#022C22]">{t.plan_name}</div>
                      <div className="text-xs text-[#64748B]">{new Date(t.created_at).toLocaleString()}</div>
                    </div>
                    <div className="col-span-3 text-[#3F6255]">
                      <div>{t.customer_email || "—"}</div>
                      <div className="text-xs">{t.customer_name || ""}</div>
                    </div>
                    <div className="col-span-2 text-[#022C22] font-medium">{fmtINR(t.amount)}</div>
                    <div className="col-span-3">
                      <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${
                        t.payment_status === "paid"
                          ? "bg-[#D1FAE5] text-[#065F46]"
                          : t.payment_status === "initiated"
                          ? "bg-[#FEF3C7] text-[#B45309]"
                          : "bg-[#F3F4F6] text-[#374151]"
                      }`}>
                        {t.payment_status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
